import React from 'react';
import { Link } from 'react-router-dom';
import {
	useConfiguration,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useKatastrNemovitostiContextInstance } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	CommaSpace,
	ContentLayout,
	ErrorStatusBanner,
	Icon,
	InfoBanner,
	LayoutGap,
	Space,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { CadastreOfRealEstateFilter } from './CadastreOfRealEstateFilter';
import {
	CadastreOfRealEstateItemsSkeleton,
	CadastreOfRealEstateSkeleton,
} from './CadastreOfRealEstateSkeleton';

export interface CadastreOfRealEstateProps {
	code: PageCode;
}

export const CadastreOfRealEstateView = ({
	code,
}: CadastreOfRealEstateProps) => {
	const { mujKatastrUrl } = useConfiguration();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const { realEstates, controls, queryLoading, noRealEstates } =
		useKatastrNemovitostiContextInstance();

	if (controls.initialLoading) {
		return <CadastreOfRealEstateSkeleton />;
	}

	if (controls.initialError) {
		return <ErrorStatusBanner code={code} />;
	}

	return (
		<ContentLayout>
			<CadastreOfRealEstateFilter code={code} />
			<LayoutGap gap="l">
				{queryLoading && <CadastreOfRealEstateItemsSkeleton />}
				<Tiles>
					{noRealEstates ? (
						<InfoBanner headline={ts('info.nejsou-evidovany-zadne-subjekty')} />
					) : (
						realEstates?.map((realEstate) => (
							<Tile
								key={`${realEstate.obec}-${realEstate.katastr}`}
								name={`${realEstate.obec}, ${realEstate.katastr}`}
								to={realEstate.link}
								target="_blank"
								icon={{ name: 'box-arrow-up-right', type: 'components' }}>
								<p className="text-(--text-secondary)!">
									{ts('zaznam.list-vlastnictvi')}
									<Space />
									<strong>{realEstate.lv}</strong>
									{realEstate.podil?.citatel &&
										realEstate.podil?.jmenovatel && (
											<span>
												<CommaSpace />
												{ts('zaznam.podil')}
												<Space />
												<strong>
													{realEstate.podil?.citatel}
													<Space />/<Space />
													{realEstate.podil?.jmenovatel}
												</strong>
											</span>
										)}
								</p>
							</Tile>
						))
					)}
				</Tiles>
				<ButtonGroupLayout>
					<Button
						asChild
						type="outlined"
						color="primary"
						isExpandedMobile
						iconEnd={
							<Icon
								type="components"
								name="box-arrow-up-right"
							/>
						}
						aria-label={ts('akce.prejit-na-muj-katastr')}>
						<Link
							to={mujKatastrUrl}
							target="_blank">
							{ts('akce.prejit-na-muj-katastr')}
						</Link>
					</Button>
				</ButtonGroupLayout>
			</LayoutGap>
		</ContentLayout>
	);
};
